import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Search, Loader2, Building2, MapPin, Sparkles, ArrowRight, Plus, Globe, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { collection, query, where, getDocs, limit } from 'firebase/firestore';
import { db } from '../core/services/firebase/config';

interface ExpandedSearchProps {
  isOpen: boolean;
  onClose: () => void;
  dominantColor?: string;
}

export function ExpandedSearch({ isOpen, onClose, dominantColor = 'rgba(20, 20, 30, 0.9)' }: ExpandedSearchProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [suggestions, setSuggestions] = useState<any[]>([]);
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  useEffect(() => {
    const delayDebounceFn = setTimeout(async () => {
      if (searchTerm.length >= 2) {
        setIsLoading(true);
        try {
          const q = query(
            collection(db, 'tenants'),
            where('businessName', '>=', searchTerm),
            where('businessName', '<=', searchTerm + '\uf8ff'),
            limit(5)
          );
          const snapshot = await getDocs(q);
          const results = snapshot.docs.map(doc => ({
            id: doc.id,
            name: doc.data().businessName,
            type: doc.data().tenantType,
            location: `${doc.data().location?.city || ''}, ${doc.data().location?.state || ''}`
          }));
          setSuggestions(results);
        } catch (error) {
          console.error('Search error:', error);
        } finally {
          setIsLoading(false);
        }
      } else {
        setSuggestions([]);
      }
    }, 300);
    return () => clearTimeout(delayDebounceFn);
  }, [searchTerm]);

  const handleSearch = () => {
    if (searchTerm.trim()) {
      navigate(`/buscar?q=${encodeURIComponent(searchTerm)}`);
      onClose();
      setSearchTerm('');
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleSearch();
    if (e.key === 'Escape') onClose();
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[290]"
          />

          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            className="fixed inset-x-4 bottom-28 md:bottom-24 max-w-2xl mx-auto rounded-3xl overflow-hidden shadow-2xl z-[300]"
            style={{ 
              backgroundColor: dominantColor,
              backdropFilter: 'blur(30px)',
              border: '1px solid rgba(255,255,255,0.1)'
            }}
          >
            {/* Header / Close (Mobile) */}
            <div className="absolute top-2 right-2 md:hidden">
                 <button onClick={onClose} className="p-2 text-white/50 hover:text-white bg-white/5 rounded-full">
                    <X size={16} />
                 </button>
            </div>

            {/* Resultados */}
            {(isLoading || suggestions.length > 0 || searchTerm.length >= 2) && (
              <div className="p-4 border-b border-white/10 max-h-[40vh] overflow-y-auto">
                {isLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="animate-spin text-brand-gold" size={24} />
                  </div>
                ) : suggestions.length > 0 ? (
                  <div className="space-y-2">
                    <p className="text-[10px] font-bold text-white/30 uppercase tracking-widest mb-3">
                      Resultados
                    </p>
                    {suggestions.map((item) => (
                      <button
                        key={item.id}
                        onClick={() => {
                          navigate(`/empresas/${item.id}`);
                          onClose();
                        }}
                        className="w-full flex items-center gap-3 p-3 rounded-xl hover:bg-white/10 transition-all text-left"
                      >
                        <div className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center">
                          <Building2 size={18} className="text-white/50" />
                        </div>
                        <div>
                          <p className="text-sm font-bold text-white">{item.name}</p>
                          <p className="text-xs text-white/40 flex items-center gap-1">
                            <MapPin size={10} /> {item.location}
                          </p>
                        </div>
                      </button>
                    ))}
                  </div>
                ) : (
                  <div className="py-8 text-center">
                    <Sparkles className="mx-auto text-brand-gold/30 mb-2" size={24} />
                    <p className="text-sm text-white/30">Buscando...</p>
                  </div>
                )}
              </div>
            )}

            {/* Input */}
            <div className="flex items-center gap-3 p-4 pt-6 md:pt-4">
              <Search size={22} className="text-brand-gold shrink-0 drop-shadow-glow" />
              <input
                ref={inputRef}
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Pergunte ao Manél..."
                className="flex-1 bg-transparent border-none focus:ring-0 text-white placeholder-white/30 text-lg font-medium"
              />
              {searchTerm.length > 0 && (
                <motion.button
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  onClick={handleSearch}
                  className="w-10 h-10 bg-brand-gold text-brand-midnight rounded-full flex items-center justify-center"
                >
                  <ArrowRight size={18} />
                </motion.button>
              )}
            </div>

            {/* Quick Actions */}
            <div className="flex items-center gap-4 px-4 py-3 border-t border-white/5 bg-white/5">
              <button className="flex items-center gap-2 text-[10px] font-bold text-white/40 hover:text-white transition-colors uppercase">
                <Plus size={12} /> Pesca
              </button>
              <button className="flex items-center gap-2 text-[10px] font-bold text-white/40 hover:text-white transition-colors uppercase">
                <Globe size={12} /> Destinos
              </button>
              <span className="ml-auto text-[9px] text-white/20 hidden md:block">ESC para fechar</span>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
